<template>
	<div class="tables-select">
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="active" @toggling-popup="v => $emit('toggling-popup', v)">
			<div class="tables-select-content" v-if="active">
				<ModalHeader :title="getString('choose_table')" />
				<div class="room-container">
					<AppList :items="roomsElements">
						<template #room="{item}">
							<div class="tables-container">
								<div v-if="item.tables.length > 0" class="table" v-for="table in item.tables" :key="table.room_table_id" :class="{selected: isTableSelected(table), free: !table.openDate, occupied: !!table.openDate && !table.prebillRequested, closing: !!table.openDate && table.prebillRequested}" @click="selectTable(table, item)">
									<div class="table-name">
										<p>{{ table.table_name }}</p>
										<div class="table-people">
											<div class="table-icon" v-html="svgStore.get('iconUser')"></div>
											<div class="people-num">{{ table.seat_number ? table.seat_number : '1' }}</div>
										</div>
									</div>
								</div>
								<div v-else>
									<p class="no-tables">{{ getString('no_tables') }}</p>
								</div>
							</div>
						</template>
						<template #tables-select-slot="{item}">
							<div class="row header-row" @click="clickRoomDropdown(item)">
								<p class="chevron-p">{{ item.name }}</p>
								<div class="chevron-container">
									<AppChevronIcon :direction="item.active ? 'up' : 'down'" />
								</div>
							</div>
						</template>
					</AppList>
				</div>
			</div>
		</AppBottomSheet>
		<AppPopup
			:title="getString('how_many_people')"
			:bodyText="getString('how_many_people_explain')"
			ref="confirm-cover"
			:buttons="popupButtons" >
			<div class="container-input mb-15">
				<TouchNumberInput :min="0" :value="openingTable && openingTable.seat_number ? openingTable.seat_number : 1" @changed-value="v => coversNum = v" />
			</div>
		</AppPopup>
	</div>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useLanguageStore, useSvgStore} from "@/shared/stores";
import {useCounterStore} from "@/console-new/stores/counter.store";
import AppList from "@/shared/components/AppList.vue";
import {useRoomsStore, useUserStore} from "@/console-new/stores";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AvailabilityDot from "@/console-new/components/config/menu/AvailabilityDot.vue";
import API from "@/shared/helpers/API.js";
import AppPopup from "@/shared/components/AppPopup.vue";
import TouchNumberInput from "@/console-new/components/shared/TouchNumberInput.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		const counterStore = useCounterStore();
		const roomsStore = useRoomsStore();
		const userStore = useUserStore();
		const svgStore = useSvgStore();
		return {getString: languageStore.getString, languageStore, counterStore, roomsStore, userStore, svgStore};
	},
	name: "TablesSelect",
	components: {TouchNumberInput, AppPopup, AvailabilityDot, AppChevronIcon, AppList, ModalHeader, AppBottomSheet},
	mixins: [WindowWidth],
	emits: ["toggling-popup", "selected-table"],
	props: {
		active: {
			type: Boolean,
			required: true,
		},
	},
	data() {
		return {
			openingTable: null,
			coversNum: 1
		}
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		roomsElements() {
			return this.roomsStore.roomsTables ? this.roomsStore.roomsTables.map(room => {
				return {
					...room,
					title: room.name,
					customSlot: "tables-select-slot",
					dropdownSlot: "room",
					active: this.roomsStore.roomsTables.length === 1 ? true : room.active,
				}
			}) : [];
		},
		popupButtons() {
			return [
				{text: this.getString('cancel'), callback: this.cancelOpenTable, negative: true},
				{text: this.getString('confirm'), callback: this.openTable}
			]
		}
	},
	methods: {
		clickRoomDropdown(room) {
			if(!room.active) {
				this.roomsStore.updateRoomStatus(room);
			}
			this.roomsElements.forEach(r => {
				if (r.room_id === room.room_id) {
					room.active = !room.active;
				} else {
					r.active = false;
				}
			})
		},
		selectTable(table, room = null) {
			if(this.counterStore.editOrder) {
				this.counterStore.editOrder.order_mode = "qr";
				this.counterStore.editOrder.room_table_id = table.room_table_id;
			} else {
				if(table.current_course) {
					this.counterStore.selectedTable = table;
					this.counterStore.selectedMode = "qr";
					this.counterStore.deliveryCosts = 0;
					this.counterStore.takeawayCosts = 0;
					if(table.current_course > this.counterStore.maxCourse) {
						this.counterStore.maxCourse = table.current_course;
						this.counterStore.selectedCourse = table.current_course;
					}
				} else {
					if(room && room?.cover_charge !== null) {
						this.openingTable = table;
						this.coversNum = table.seat_number ? table.seat_number : 1;
						this.$refs['confirm-cover'].toggleStatus(true);
						return;
					} else {
						this.counterStore.selectedTable = table;
						this.counterStore.selectedMode = "qr";
						this.counterStore.deliveryCosts = 0;
						this.counterStore.takeawayCosts = 0;
						this.counterStore.coverNum = 0;
						this.counterStore.coverCharge = 0;
					}
				}
				this.$emit("selected-table");
			}
			this.$emit("toggling-popup", false);
		},
		isTableSelected(table) {
			return this.counterStore.selectedTable && table.room_table_id === this.counterStore.selectedTable.room_table_id;
		},
		cancelOpenTable() {
			this.openingTable = null;
			this.$refs['confirm-cover'].toggleStatus(false);
		},
		openTable() {
			this.counterStore.selectedTable = this.openingTable;
			this.counterStore.selectedMode = "qr";
			this.counterStore.deliveryCosts = 0;
			this.counterStore.takeawayCosts = 0;
			let foundRoom = this.roomsStore.roomFromTableId(this.openingTable?.room_table_id);
			if(foundRoom && foundRoom?.cover_charge !== null) {
				this.counterStore.coverNum = this.coversNum;
				this.counterStore.coverCharge = foundRoom.cover_charge;
			} else {
				this.counterStore.coverNum = 0;
				this.counterStore.coverCharge = 0;
			}
			this.$emit("selected-table");
			this.$refs['confirm-cover'].toggleStatus(false);
			this.$emit("toggling-popup", false);
			this.openingTable = null;
		}
	},
	watch: {
		active(val) {
			if(val) {
				this.roomsElements.forEach(room => {
					if(room.active) {
						this.roomsStore.updateRoomStatus(room);
					}
				});
			}
		}
	}
}
</script>

<style scoped lang="scss">
.chevron-p {
	width: calc(100% - 50px);
}
.header-row {
	display: flex;
	align-items: center;
	user-select: none;
	justify-content: space-between;
	cursor: pointer;
	padding: 6px 0;
}
.tables-container {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 10px;

	.table {
		width: calc((100% / 4) - 10px);
		border-radius: 13px;
		cursor: pointer;
		transition: box-shadow 0.2s ease-in-out;
		box-shadow: 0 2px 3px var(--color-box-shadow-3);
		font-size: 16px;
		display: flex;
		justify-content: space-between;
		align-items: center;
		--card-color: var(--color-bg);
		--accent-color: var(--color-primary);
		--gradient-start-color:var(--color-primary);
		background-color: var(--card-color);

		&:hover {
			box-shadow: 0 2px 3px rgba(0, 0, 0, 0);
		}

		.table-name {
			width: 100%;
			display: flex;
			justify-content: space-between;
			align-items: center;

			p {
				@include truncateString(1);
				padding: 8px 4px 8px 12px;
			}

			.table-people {
				display: flex;
				align-items: center;
				gap: 4px;
				font-size: 14px;
				color: var(--color-text);
				background-color: var(--color-alpha-2);
				height: 30px;
				margin-right: 12px;
				border-radius: 7px;
				box-shadow: inset 0 2px 3px var(--color-box-shadow-3);
				padding: 0 8px;

				.table-icon {
					width: 14px;
					height: 14px;

					&:deep(svg) {
						width: 14px;
						height: 14px;

						.fill {
							fill: var(--color-primary);
						}

						.stroke {
							stroke: var(--color-primary);
						}
					}
				}

				.people-num {
					font-size: 16px;
					color: var(--color-primary);
					font-weight: 500;
				}
			}
		}

		.table-course {
			border-radius: 13px;
			border: 1px solid $primary;
			padding: 2px 4px;
			min-width: 26px;
			text-align: center;
			font-size: 12px;
			color: $primary;
			cursor: pointer;
			transition: background-color 0.25s ease-in-out;
			display: flex;
			justify-content: center;
			align-items: center;
			user-select: none;
		}

		&.selected {
			background: var(--color-active-button);
			border: 1px solid var(--color-primary);
			box-shadow: 0 2px 3px rgba(7, 82, 163, 0);
		}

		&.free {
			--card-color: var(--color-table-free);
			--accent-color: var(--color-table-gradient-start-free);
			--gradient-start-color: var(--color-table-gradient-end-free);
		}

		&.occupied {
			--card-color: var(--color-table-occupied);
			--accent-color: var(--color-table-gradient-start-occupied);
			--gradient-start-color: var(--color-table-gradient-end-occupied);
		}

		&.closing {
			--card-color: var(--color-table-closing);
			--accent-color: var(--color-table-gradient-start-closing);
			--gradient-start-color: var(--color-table-gradient-end-closing);
		}

		@media (max-width: 1440px) {
			width: calc((100% / 3) - 10px);
		}

		@media (max-width: 768px) {
			width: calc((100% / 2) - 10px);
		}

		@media (max-width: 450px) {
			width: calc((100%) - 10px);
		}

	}
}
</style>